/**
 * MYB-121: basket <-> configurator integration.
 *
 * The shop renders the basket in two places and this module reacts to both:
 *  - the full basket page: <tr data-id="<lineId>" data-product-id="<productId>">
 *  - the offcanvas mini-cart: .basket-contain .basket-product-list li[data-product-id]
 *    (the line id is read from its "/basket/remove/<lineId>" link)
 *
 * Using `store.cart.basketConfigurations` (resolved from the saved basket
 * configuration) it:
 *  1. adds an "Edit configuration" link (full basket: under the variant container
 *     `.variant-list`/`.variant`;
 *     mini-cart: before the remove link, with a Font Awesome icon),
 *  2. opens the configurator edit window when a configured product is clicked,
 *  3. removes the extra products together with the main product (full basket).
 *
 * The mini-cart is fully rebuilt by the shop's `digiSidecartLite.renderList`
 * on every cart sync, so the mini-cart is injected on the `mmz:basketRendered`
 * event the shop dispatches synchronously at the end of that render. Injecting
 * inside that event keeps us within the shop's render window, so its own
 * MutationObserver ignores our node and does not trigger another render.
 *
 * Optional env overrides: REACT_APP_BASKET_PATH, REACT_APP_BASKET_ITEM_SELECTOR,
 * REACT_APP_BASKET_VARIANT_SELECTOR.
 */

import { store } from '../stores/Store';
import { _t, lang } from '../utils/Lang';
import { isFeatureEnabled } from '../utils/Features';
import { MATTRESS_WITH_BED_OPTION_ID } from '../utils/ConfigurationOptions';
import './basketEditLinks.scss';

const DEFAULT_BASKET_PATH = '/basket';
const EDIT_BUTTON_CLASS = 'configurator-edit-link';
const QUANTITY_LOCKED_CLASS = 'configurator-quantity-locked';
const QUANTITY_LOADING_CLASS = 'myb-basket-quantity-loading';
const QUANTITY_LOADING_TIMEOUT = 4000;
// MYB-144: badge shown on the mattress of a configured bed set.
const MATTRESS_DISCOUNT_BADGE_CLASS = 'configurator-mattress-discount-badge';
const MINI_ICON = '<i class="fa-regular fa-pen-to-square"></i>';
// Dispatched by the shop theme (digiSidecartLite.applyData) after renderList.
const BASKET_RENDERED_EVENT = 'mmz:basketRendered';

const SELECTORS = {
    row: process.env.REACT_APP_BASKET_ITEM_SELECTOR || 'tr[data-id][data-product-id]',
    mini: '.basket-contain .basket-product-list li[data-product-id]',
    variantList: process.env.REACT_APP_BASKET_VARIANT_SELECTOR || '.variant-list, .variant',
    // Any product link (desktop/mobile name) or the product image.
    productTrigger: 'a, td.img img',
    // Name cells: desktop `td.name` and the mobile name block inside `td.quantity`.
    nameCell: 'td.name',
    mobileName: '.mobile-details.name-details',
    remove: 'a.prodremove',
    // Quantity is managed by the configurator for configured lines, so the
    // shop's +/- controls are disabled.
    quantityWrap: '.shaded_inputwrap',
    quantityInput: 'input[name^="quantity_"]',
    quantityButton: '.button-change',
    quantityLocked: `.${QUANTITY_LOCKED_CLASS}`,
    miniRemove: '.remove-product',
    miniRemoveLink: '.remove-product a[href*="/basket/remove/"]',
    miniVariantsToggle: '.digi-toggle-variants-head',
};

function debounce(fn, wait) {
    let timer;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn(...args), wait);
    };
}

class BasketEditLinks {
    constructor() {
        this.pendingRemoval = false;
        this.observer = null;
        this._quantityLoadingTimer = null;
        this.onClick = this.onClick.bind(this);
        this.onQuantityInput = this.onQuantityInput.bind(this);
        this.onBasketRendered = this.onBasketRendered.bind(this);
        // The full-basket rows are server-rendered; a debounced observer is
        // enough. The mini-cart is NOT handled here (see onBasketRendered).
        this.scheduleRowsRender = debounce(() => this.applyRows(), 200);
    }

    get isEnabled() {
        return isFeatureEnabled();
    }

    get configurations() {
        return store.cart.basketConfigurations || [];
    }

    async init() {
        if (!this.isEnabled) {
            return;
        }
        console.log('[basketEditLinks] init');
        // MYB-144: the quantity lock needs the resolved configurations, which
        // only arrive after the async cart load. To avoid a moment where the
        // server-rendered quantity controls are interactable, disable them
        // synchronously (basket page only) until the configurations resolve.
        this.startQuantityLoadingState();
        // Attach listeners synchronously, before the async cart load, so we do
        // not miss the shop's first mini-cart render event.
        document.addEventListener('click', this.onClick, true);
        document.addEventListener(BASKET_RENDERED_EVENT, this.onBasketRendered);
        // MYB-144: keep the linked extras in sync while the main quantity
        // changes, before the theme's recalc form is submitted.
        document.addEventListener('input', this.onQuantityInput, true);
        document.addEventListener('change', this.onQuantityInput, true);
        try {
            await store.cart.loadCart();
            this.apply();
            this.observer = new MutationObserver(this.scheduleRowsRender);
            this.observer.observe(document.body, { childList: true, subtree: true });
        } catch (err) {
            console.error('[basketEditLinks] init failed', err);
        } finally {
            this.clearQuantityLoadingState();
        }
    }

    /**
     * MYB-144: disables the shop's quantity controls while the basket
     * configurations load, so a configured extra is never interactable before it
     * is locked. A timeout releases the state if the load never resolves.
     */
    startQuantityLoadingState() {
        if (typeof document === 'undefined' || !isBasketPage()) {
            return;
        }
        document.documentElement.classList.add(QUANTITY_LOADING_CLASS);
        clearTimeout(this._quantityLoadingTimer);
        this._quantityLoadingTimer = setTimeout(() => this.clearQuantityLoadingState(), QUANTITY_LOADING_TIMEOUT);
    }

    clearQuantityLoadingState() {
        clearTimeout(this._quantityLoadingTimer);
        this._quantityLoadingTimer = null;
        if (typeof document !== 'undefined') {
            document.documentElement.classList.remove(QUANTITY_LOADING_CLASS);
        }
    }

    apply() {
        this.applyRows();
        this.applyMiniCart();
        this.clearQuantityLoadingState();
    }

    /** Full basket page rows. */
    applyRows() {
        if (!this.isEnabled) {
            return;
        }
        const index = this.buildIndex();
        if (index.size === 0) {
            this.unlockAllQuantities();
            return;
        }
        const extraRows = new Set();
        document.querySelectorAll(SELECTORS.row).forEach((el) => {
            const entry = this.getPrimaryEntry(this.getEntriesForItem(el, index));
            if (!entry) {
                return;
            }
            this.rewriteProductLinks(el, entry.config);
            // Lock the quantity of the extra products; the main product's
            // quantity stays editable (it's how the shop changes the bundle
            // count).
            if (entry.isMain) {
                this.injectRowEditButtons(el, entry.config);
            } else {
                extraRows.add(el);
                this.lockQuantity(el);
                // MYB-144: the mattress of a bed set carries option 169; show the
                // 10% discount badge on its row.
                if (this.hasMattressWithBedDiscount(entry)) {
                    this.injectMattressDiscountBadge(el);
                }
            }
        });
        // Release locks that are no longer backed by a configuration (e.g. a
        // line removed or a stale lock from an earlier render).
        document.querySelectorAll(`${SELECTORS.quantityWrap}.${QUANTITY_LOCKED_CLASS}`).forEach((wrap) => {
            const row = wrap.closest(SELECTORS.row);
            if (!row || !extraRows.has(row)) {
                this.unlockQuantity(wrap);
            }
        });
    }

    /**
     * Mini-cart items. Called from the shop's `mmz:basketRendered` event, which
     * fires synchronously inside the shop's render (while its own observer is
     * paused), so injecting here does not trigger another render loop.
     */
    applyMiniCart() {
        if (!this.isEnabled) {
            return;
        }
        const index = this.buildIndex();
        if (index.size === 0) {
            return;
        }
        document.querySelectorAll(SELECTORS.mini).forEach((el) => {
            const entry = this.getPrimaryEntry(this.getEntriesForItem(el, index));
            if (!entry || !entry.isMain) {
                return;
            }
            this.injectMiniEditLink(el, entry.config);
        });
    }

    onBasketRendered() {
        console.log('[basketEditLinks] mmz:basketRendered');
        this.applyMiniCart();
    }

    /**
     * Builds a map of basket line id -> [{ config, item, isMain }]. The shop
     * merges identical lines, so a line can be owned by several bundles; every
     * owner is kept. The main item is the one flagged with role "main", falling
     * back to the configured product id and finally to the first item.
     */
    buildIndex() {
        const index = new Map();
        this.configurations.forEach((configuration) => {
            const items = configuration.basketItems || [];
            if (items.length === 0) {
                return;
            }
            const main = items.find((item) => item.role === 'main')
                || items.find((item) => String(item.productId) === String(configuration.productId))
                || items[0];
            items.forEach((item) => {
                if (item.id === undefined || item.id === null) {
                    return;
                }
                const key = String(item.id);
                if (!index.has(key)) {
                    index.set(key, []);
                }
                index.get(key).push({
                    config: configuration,
                    item,
                    isMain: item === main,
                });
            });
        });
        return index;
    }

    /**
     * Rewrites the href of every product link in a configured full-basket row so
     * it points to the configurator edit window. More robust than click handling
     * because the shop may intercept/navigate the original link itself.
     */
    rewriteProductLinks(row, configuration) {
        const editUrl = this.buildEditUrl(configuration);
        row.querySelectorAll('a').forEach((link) => {
            if (link.classList.contains(EDIT_BUTTON_CLASS)) {
                return;
            }
            if (link.closest(SELECTORS.remove) || link.closest('td.actions')) {
                return;
            }
            if (link.getAttribute('href') === editUrl) {
                return;
            }
            link.setAttribute('href', editUrl);
            link.setAttribute('data-configurator-link', '1');
            console.log('[basketEditLinks] rewrote product link', { href: editUrl });
        });
    }

    injectRowEditButtons(row, configuration) {
        // The shop theme's `productMMQ.cleanBasket()` rewrites `td.name .variant`
        // with `$(el).text(...)`: it reads the container's text (swallowing any
        // link inside it) and then replaces its children with a single text node.
        // Inject the link into the name cell (a sibling of `.variant`) instead, so
        // the theme's text rewrite never touches it. Idempotent per name cell.
        const scopes = new Set();
        row.querySelectorAll(SELECTORS.variantList).forEach((container) => {
            const scope = container.closest(`${SELECTORS.nameCell}, ${SELECTORS.mobileName}`)
                || container.parentElement;
            if (scope) {
                scopes.add(scope);
            }
        });
        scopes.forEach((scope) => {
            if (!this.hasEditLink(scope, configuration.id)) {
                scope.appendChild(this.createEditButton(configuration));
            }
        });
    }

    /**
     * MYB-144: does this configured item carry the "mattress in a set with the
     * bed" option (169)? The value is forced true by the configurator when a
     * mattress is added, so we read it from the stored add payload and fall back
     * to the saved configuration JSON.
     */
    hasMattressWithBedDiscount(entry) {
        const { item, config } = entry || {};
        if (this.isMattressDiscountOption(item?.options)) {
            return true;
        }
        if (this.isMattressDiscountOption(item?.addPayload?.options)) {
            return true;
        }
        const extra = config?.configuration?.extra || [];
        return extra.some((step) => (step.productsOptions || []).some((product) => (
            String(product.id) === String(item?.productId)
            && this.isMattressDiscountOption(product.options)
        )));
    }

    isMattressDiscountOption(options) {
        if (!options) {
            return false;
        }
        const value = options[MATTRESS_WITH_BED_OPTION_ID]
            ?? options[String(MATTRESS_WITH_BED_OPTION_ID)];
        return value === true || value === 1 || value === '1'
            || value === 'tak' || value === 'yes';
    }

    /**
     * MYB-144: inserts the "-10% with the bed" badge above the product link in
     * the row's name. The full-basket row renders the name twice: the desktop
     * `td.name` and the mobile `.mobile-details.name-details` (inside
     * `td.quantity`), so the badge is injected into both. Idempotent per
     * container so the debounced observer never duplicates it.
     */
    injectMattressDiscountBadge(row) {
        [SELECTORS.nameCell, SELECTORS.mobileName].forEach((selector) => {
            const container = row.querySelector(selector);
            if (!container || container.querySelector(`.${MATTRESS_DISCOUNT_BADGE_CLASS}`)) {
                return;
            }
            const badge = document.createElement('span');
            badge.className = MATTRESS_DISCOUNT_BADGE_CLASS;
            badge.textContent = _t('mattress_with_bed_discount');
            container.insertBefore(badge, container.firstChild);
        });
    }

    /**
     * MYB-121: the quantity of an *extra* product of a configured line is owned
     * by the basket configuration (the configurator creates/removes the whole
     * bundle), so the shop's +/- controls are disabled and the value is
     * read-only. The main product's quantity stays editable. The wrapper is
     * dimmed and shows a "not-allowed" cursor (see basketEditLinks.scss).
     */
    lockQuantity(row) {
        row.querySelectorAll(SELECTORS.quantityWrap).forEach((wrap) => {
            wrap.classList.add(QUANTITY_LOCKED_CLASS);
        });
        row.querySelectorAll(SELECTORS.quantityInput).forEach((input) => {
            input.setAttribute('readonly', 'readonly');
            input.setAttribute('tabindex', '-1');
        });
    }

    /** Releases a quantity lock (line no longer part of a configuration). */
    unlockQuantity(wrap) {
        wrap.classList.remove(QUANTITY_LOCKED_CLASS);
        wrap.querySelectorAll(SELECTORS.quantityInput).forEach((input) => {
            input.removeAttribute('readonly');
            input.removeAttribute('tabindex');
        });
    }

    unlockAllQuantities() {
        document.querySelectorAll(`${SELECTORS.quantityWrap}.${QUANTITY_LOCKED_CLASS}`).forEach((wrap) => {
            this.unlockQuantity(wrap);
        });
    }

    /**
     * MYB-144: the main product quantity is the bundle count. The theme updates
     * the input and, after a 1s debounce, clicks the basket recalc form, which
     * submits every `quantity_<lineId>` input at once. We do not block any of
     * that: we only write the scaled quantities of the linked lines (extras and
     * lines shared between bundles) into their inputs so the single form submit
     * carries them. The changed configuration quantity is persisted separately.
     */
    onQuantityInput(event) {
        if (!this.isEnabled || !(event.target instanceof Element)) {
            return;
        }
        const input = event.target.closest(SELECTORS.quantityInput);
        if (!input) {
            return;
        }
        const index = this.buildIndex();
        if (index.size === 0) {
            return;
        }
        const context = this.findItemContext(input);
        const entry = this.getPrimaryEntry(this.getEntriesForItem(context, index));
        if (!entry || !entry.isMain) {
            return;
        }
        const newTotal = parseInt(input.value, 10);
        if (Number.isNaN(newTotal)) {
            return;
        }
        // Persist on the committed value (change), not on every keystroke.
        this.syncMainQuantities(context, newTotal, { persist: event.type === 'change' });
    }

    /**
     * Computes and writes the linked lines' quantities for a main-line change.
     * The theme's recalc form submits them; this performs no basket API call
     * itself (it only persists the configuration quantity).
     */
    syncMainQuantities(row, newTotal, { persist = false } = {}) {
        const mainLineId = this.getLineId(row);
        const desired = store.cart.planQuantitiesForMain(mainLineId, newTotal, { persist });
        desired.forEach((quantity, id) => {
            // Never touch the main input: the user/theme owns its value (the
            // theme increments it after this capture-phase handler runs).
            if (String(id) === String(mainLineId)) {
                return;
            }
            const el = document.querySelector(`input[name="quantity_${id}"]`);
            if (el && String(el.value) !== String(quantity)) {
                el.value = quantity;
            }
        });
    }

    /** Mini-cart: an "Edit configuration" link before the remove link. */
    injectMiniEditLink(item, configuration) {
        // Document-wide guard scoped to the mini-cart: a configuration must have
        // at most one link, even across cloned items/containers. If a link is
        // already present anywhere, drop any duplicates and do nothing.
        const selector = `.basket-contain .${EDIT_BUTTON_CLASS}[data-basket-config-id="${configuration.id}"]`;
        const existing = document.querySelectorAll(selector);
        if (existing.length > 0) {
            for (let i = 1; i < existing.length; i += 1) {
                existing[i].remove();
            }
            return;
        }

        const link = this.createEditButton(configuration, { mini: true });
        const removeWrap = item.querySelector(SELECTORS.miniRemove);
        if (removeWrap && removeWrap.parentNode === item) {
            item.insertBefore(link, removeWrap);
        } else {
            item.appendChild(link);
        }
        console.log('[basketEditLinks] injected mini-cart edit link', { basketConfigId: configuration.id });
    }

    /**
     * Idempotency guard: keeps exactly one injected link inside `container`
     * (optionally for a specific configuration). If a link is already present it
     * removes any accidental duplicates and returns true, so the caller does not
     * inject again. The link is removed by the shop when the cart is re-rendered,
     * after which it is injected again.
     */
    hasEditLink(container, configurationId = null) {
        const selector = configurationId
            ? `.${EDIT_BUTTON_CLASS}[data-basket-config-id="${configurationId}"]`
            : `.${EDIT_BUTTON_CLASS}`;
        const links = container.querySelectorAll(selector);
        if (links.length === 0) {
            return false;
        }
        for (let i = 1; i < links.length; i += 1) {
            links[i].remove();
        }
        return true;
    }

    createEditButton(configuration, { mini = false } = {}) {
        const link = document.createElement('a');
        link.className = mini
            ? `${EDIT_BUTTON_CLASS} ${EDIT_BUTTON_CLASS}--mini`
            : `btn btn-white ${EDIT_BUTTON_CLASS}`;
        link.href = this.buildEditUrl(configuration);
        if (mini) {
            link.innerHTML = `${MINI_ICON} ${_t('edit_configuration')}`;
        } else {
            link.textContent = _t('edit_configuration');
        }
        link.setAttribute('data-basket-config-id', configuration.id);
        return link;
    }

    /** Delegated click handler (capture) bound once on init. */
    onClick(event) {
        if (!this.isEnabled || !(event.target instanceof Element)) {
            return;
        }

        // Let the injected edit buttons use their own href.
        if (event.target.closest(`.${EDIT_BUTTON_CLASS}`)) {
            return;
        }

        const index = this.buildIndex();
        if (index.size === 0) {
            return;
        }

        // Configured lines have a locked quantity; swallow any click on it (the
        // buttons also use pointer-events: none in CSS as a first line of
        // defence).
        const lockedQuantity = event.target.closest(SELECTORS.quantityLocked);
        if (lockedQuantity) {
            const context = this.findItemContext(lockedQuantity);
            if (this.getEntriesForItem(context, index).length > 0) {
                this.stopEvent(event);
                return;
            }
        }

        // The main product quantity is the bundle count. The +/- buttons update
        // it via the theme (`MMZS.btnQuantityChange` → jQuery `trigger('change')`,
        // which native `addEventListener` never receives), so compute the next
        // value here and sync the linked lines before the theme's debounced
        // recalc submits the form. The click is NOT consumed: the theme still
        // updates the input and schedules the recalc.
        const quantityButton = event.target.closest(SELECTORS.quantityButton);
        if (quantityButton) {
            const context = this.findItemContext(quantityButton);
            const entry = this.getPrimaryEntry(this.getEntriesForItem(context, index));
            if (context && context.matches(SELECTORS.row) && entry && entry.isMain) {
                const input = context.querySelector(SELECTORS.quantityInput);
                if (input) {
                    const current = parseInt(input.value, 10) || 1;
                    const delta = String(quantityButton.textContent || '').trim() === '+' ? 1 : -1;
                    this.syncMainQuantities(context, Math.max(1, current + delta), { persist: true });
                }
            }
            return;
        }

        // Removing any configured line removes every bundle that owns it. The
        // shop merges shared lines, so deleting one invalidates all its owners.
        const removeLink = event.target.closest(`${SELECTORS.remove}, ${SELECTORS.miniRemoveLink}`);
        if (removeLink) {
            const context = this.findItemContext(removeLink);
            const entries = this.getEntriesForItem(context, index);
            if (entries.length > 0) {
                // Fully consume the event so the shop's own remove handler does
                // not navigate/AJAX in parallel with our cascade removal.
                this.stopEvent(event);
                this.removeConfiguredLine(removeLink, context);
            }
            return;
        }

        // The mini-cart variants toggle must keep working.
        if (event.target.closest(SELECTORS.miniVariantsToggle)) {
            return;
        }

        // Any product link (or full-basket product image) opens the edit window.
        const trigger = event.target.closest(SELECTORS.productTrigger);
        if (!trigger || trigger.closest('td.actions') || trigger.closest(SELECTORS.remove)) {
            return;
        }
        // Never hijack a remove link (e.g. the mini-cart "usuń").
        if (trigger.closest(SELECTORS.miniRemove) || String(trigger.getAttribute('href') || '').includes('/basket/remove/')) {
            return;
        }
        const context = this.findItemContext(trigger);
        if (!context) {
            return;
        }
        const entry = this.getPrimaryEntry(this.getEntriesForItem(context, index));
        if (entry) {
            this.stopEvent(event);
            this.goToEdit(entry.config);
        }
    }

    /** Fully consume an event we handled ourselves. */
    stopEvent(event) {
        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === 'function') {
            event.stopImmediatePropagation();
        }
    }

    /** Nearest configured container: a full-basket row or a mini-cart item. */
    findItemContext(el) {
        return el.closest(SELECTORS.row) || el.closest(SELECTORS.mini) || null;
    }

    /** Every owner of the line in the given container. */
    getEntriesForItem(el, index) {
        if (!el) {
            return [];
        }
        const lineId = this.getLineId(el);
        if (!lineId) {
            return [];
        }
        return index.get(String(lineId)) || [];
    }

    /** Preferred owner: the one whose item is the bundle's main product. */
    getPrimaryEntry(entries) {
        return (entries || []).find((entry) => entry.isMain) || (entries || [])[0] || null;
    }

    /** Line id from the row attribute or the mini-cart remove link. */
    getLineId(el) {
        const direct = el.getAttribute('data-id') || el.getAttribute('data-basket-item-id');
        if (direct) {
            return direct;
        }
        const removeLink = el.querySelector('a[href*="/basket/remove/"]');
        if (removeLink) {
            const match = removeLink.getAttribute('href').match(/\/basket\/remove\/([^/?#]+)/);
            if (match) {
                return match[1];
            }
        }
        return null;
    }

    /**
     * Removing a configured line deletes every bundle that owns it (the shop
     * merges shared lines) and reconciles the remaining bundles. Removing a line
     * can cascade to several products, so once everything is done the page is
     * reloaded to guarantee the rendered basket (full page and offcanvas
     * mini-cart) matches the server state.
     */
    async removeConfiguredLine(removeLink, context) {
        if (this.pendingRemoval) {
            return;
        }
        this.pendingRemoval = true;
        this.setBusy(removeLink, true);

        const lineId = context ? this.getLineId(context) : null;
        const productId = context ? (context.getAttribute('data-product-id') || null) : null;
        const isFullBasket = Boolean(context && context.matches(SELECTORS.row));
        console.log('[basketEditLinks] removeConfiguredLine', { lineId, productId, isFullBasket });

        try {
            await store.cart.removeConfigurationsForLine(lineId, productId);
            console.log('[basketEditLinks] removed bundles for line', lineId);
        } catch (err) {
            console.error('[basketEditLinks] remove failed', err);
        } finally {
            this.pendingRemoval = false;
            window.location.reload();
        }
    }

    goToEdit(configuration) {
        const url = this.buildEditUrl(configuration);
        console.log('[basketEditLinks] open edit window', url);
        window.location.assign(url);
    }

    buildEditUrl(configuration) {
        // Keep the domain currently active on the page: use the stored
        // returnUrl path only, rebuilt against window.location.origin.
        let path = window.location.pathname;
        try {
            const returnUrl = new URL(configuration.returnUrl || '', window.location.origin);
            path = returnUrl.pathname + returnUrl.search;
        } catch (err) {
            console.warn('[basketEditLinks] invalid returnUrl, using current path', configuration.returnUrl);
        }
        const url = new URL(path, window.location.origin);
        url.searchParams.set('basketConfig', configuration.id);
        url.searchParams.set('lang', lang.current);
        return url.toString();
    }

    setBusy(el, busy) {
        if (!el) {
            return;
        }
        el.style.pointerEvents = busy ? 'none' : '';
        el.style.opacity = busy ? '0.5' : '';
    }
}

export function isBasketPage() {
    if (typeof window === 'undefined') {
        return false;
    }
    const path = window.location.pathname.replace(/\/+$/, '');
    const basketPath = (process.env.REACT_APP_BASKET_PATH || DEFAULT_BASKET_PATH).replace(/\/+$/, '');
    return path === basketPath || path.endsWith(basketPath);
}

let instance = null;

const INIT_FLAG = '__MYB_BASKET_EDIT_LINKS__';

export function initBasketEditLinks() {
    if (typeof window === 'undefined') {
        return Promise.resolve();
    }
    // Guard against app.js being evaluated/initialized more than once, which
    // would otherwise attach multiple observers and inject links repeatedly.
    if (!window[INIT_FLAG]) {
        if (!instance) {
            instance = new BasketEditLinks();
        }
        window[INIT_FLAG] = instance.init();
    }
    return window[INIT_FLAG];
}
